import { memo, useEffect } from "react";
import { useI18n } from "@/i18n";
import { useTenantUsageStore } from "@/application/stores/tenantUsageStore";
import type { TenantUsageData } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

export const PlanUsagePage = memo(function PlanUsagePage() {
  const { t } = useI18n();
  const data = useTenantUsageStore(s => s.data);
  const loading = useTenantUsageStore(s => s.loading);
  const error = useTenantUsageStore(s => s.error);
  const refresh = useTenantUsageStore(s => s.refresh);

  // Always force a fresh fetch when this page mounts (navigation or F5)
  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !data) {
    return (
      <div style={{ padding: "48px 40px", fontFamily: typography.fontFamily, color: "#888" }}>
        {t.planLimit.loading}
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ padding: "48px 40px", fontFamily: typography.fontFamily, color: "#888" }}>
        {t.planLimit.errorLoading}
      </div>
    );
  }

  if (!data) return null;

  const items: { label: string; usage: number; limit: number; icon: React.ReactNode }[] = [
    { label: t.planLimit.warehouses, usage: data.usage.warehouses, limit: data.limits.warehouses, icon: <WarehouseIcon /> },
    { label: t.planLimit.products, usage: data.usage.products, limit: data.limits.products, icon: <ProductIcon /> },
    { label: t.planLimit.users, usage: data.usage.users, limit: data.limits.users, icon: <UserIcon /> },
    { label: t.planLimit.sections, usage: data.usage.sectionsByWarehouse, limit: data.limits.sectionsByWarehouse, icon: <SectionIcon /> },
  ];

  // Determine plan name from limits (heuristic)
  const planName = getPlanName(data);

  return (
    <div style={{ padding: "36px 40px 48px", display: "flex", flexDirection: "column", gap: "28px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      {/* Header */}
      <div>
        <h1 style={{ fontFamily: typography.fontFamily, fontSize: typography.size["2xl"], fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
          {t.planLimit.pageTitle}
        </h1>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "6px 0 0" }}>
          {t.planLimit.pageSubtitle}
        </p>
      </div>

      {/* Plan banner */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#f9f9f9",
        border: "1px solid #eee",
        borderRadius: "14px",
        padding: "20px 28px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{ width: "40px", height: "40px", background: colors.primary, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111" }}>
                {planName}
              </span>
              <span style={{
                fontFamily: typography.fontFamily,
                fontSize: "10px",
                fontWeight: typography.weight.bold,
                color: colors.primary,
                background: "rgba(235,81,57,0.08)",
                padding: "3px 8px",
                borderRadius: "6px",
                letterSpacing: "0.03em",
                textTransform: "uppercase",
              }}>
                {t.planLimit.currentPlan}
              </span>
            </div>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888", marginTop: "2px", display: "block" }}>
              {t.planLimit.planDescription}
            </span>
          </div>
        </div>
      </div>

      {/* Usage cards grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: "16px",
      }}>
        {items.map(item => (
          <UsageCard key={item.label} label={item.label} usage={item.usage} limit={item.limit} icon={item.icon} t={t} />
        ))}
      </div>

      {/* Feature badges */}
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111", margin: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {t.planLimit.featuresTitle}
        </h2>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <FeatureBadge label={t.planLimit.bulkImport} enabled={data.limits.bulkImportEnabled} />
          <FeatureBadge label={t.planLimit.qrBarcode} enabled={data.limits.qrBarcodeEnabled} />
          <FeatureBadge label={`${t.planLimit.analyticsType}: ${data.limits.analytics.type}`} enabled={data.limits.analytics.type !== "none"} />
          <FeatureBadge label={t.planLimit.reports} enabled={data.limits.reportsEnabled} />
          <FeatureBadge label={`${t.planLimit.reportsPerMonth}: ${data.limits.maxReportsPerMonth ?? "∞"}`} enabled={data.limits.reportsEnabled} />
        </div>
      </div>
    </div>
  );
});

/* --- Sub-components --- */

function UsageCard({ label, usage, limit, icon, t }: { label: string; usage: number; limit: number; icon: React.ReactNode; t: any }) {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : limit > 0 ? Math.min((usage / limit) * 100, 100) : 0;
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && usage >= limit;

  // Background color by state
  const bgColor = isAtLimit
    ? "rgba(239,68,68,0.04)"
    : isNearLimit
      ? "rgba(245,158,11,0.04)"
      : "#fff";

  const borderColor = isAtLimit
    ? "rgba(239,68,68,0.2)"
    : isNearLimit
      ? "rgba(245,158,11,0.2)"
      : "#eee";

  const barColor = isAtLimit ? colors.error : isNearLimit ? colors.warning : colors.primary;
  const displayLimit = isUnlimited ? "∞" : String(limit);

  return (
    <div style={{
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: "14px",
      padding: "22px 24px 18px",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      transition: `all ${transitions.base}`,
    }}>
      {/* Icon + number */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ width: "36px", height: "36px", background: isAtLimit ? "rgba(239,68,68,0.1)" : isNearLimit ? "rgba(245,158,11,0.1)" : "rgba(0,0,0,0.04)", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", color: isAtLimit ? colors.error : isNearLimit ? colors.warning : "#555" }}>
          {icon}
        </div>
        <span style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.xl,
          fontWeight: typography.weight.bold,
          color: isAtLimit ? colors.error : "#111",
          letterSpacing: "-0.02em",
        }}>
          {usage} <span style={{ color: "#bbb", fontWeight: typography.weight.medium }}>/</span> {displayLimit}
        </span>
      </div>

      {/* Label */}
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666" }}>
        {label}
      </span>

      {/* Progress bar */}
      <div style={{ width: "100%", height: "8px", background: isAtLimit ? "rgba(239,68,68,0.1)" : isNearLimit ? "rgba(245,158,11,0.1)" : "#f0f0f0", borderRadius: "4px", overflow: "hidden" }}>
        <div style={{
          width: isUnlimited ? "0%" : `${percentage}%`,
          height: "100%",
          background: barColor,
          borderRadius: "4px",
          transition: `width ${transitions.base}`,
        }} />
      </div>
    </div>
  );
}

function FeatureBadge({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <span style={{
      fontFamily: typography.fontFamily,
      fontSize: typography.size.xs,
      fontWeight: typography.weight.semibold,
      padding: "6px 14px",
      borderRadius: "20px",
      border: `1px solid ${enabled ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
      background: "transparent",
      color: enabled ? colors.success : colors.error,
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
    }}>
      {enabled ? "✓" : "✗"} {label}
    </span>
  );
}

/* --- Helpers --- */

function getPlanName(data: TenantUsageData): string {
  const { limits } = data;
  if (limits.warehouses === -1 || limits.products === -1) return "Business";
  if (limits.warehouses > 3 || limits.products > 500) return "Pro";
  return "Starter";
}

/* --- Icons --- */

function WarehouseIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
}
function ProductIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>;
}
function UserIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
}
function SectionIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>;
}
